import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Select, InputNumber, Form, Space, Input, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { UnitCategoryFloorDto, UnitBriefDto, CreateUnitCommand } from 'api/models';
import { UnitsApi } from 'api/api';
import FeatherIcon from 'feather-icons-react';
import { formatSquareMeters } from 'utility/utility';
import openNotificationWithIcon from 'utility/notification';

const unitsApi = new UnitsApi();

interface Props {
  visible: boolean;
  floor: UnitCategoryFloorDto;
  categoryId: number;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

export const FloorUnitsModal: React.FC<Props> = ({ visible, floor, categoryId, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [assignForm] = Form.useForm();
  const [newUnitForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [availableUnits, setAvailableUnits] = useState<UnitBriefDto[]>([]);
  const [activeTab, setActiveTab] = useState<'assign' | 'create'>('assign');
  const [units, setUnits] = useState<UnitCategoryFloorDto['units']>([]);

  useEffect(() => {
    if (visible) {
      fetchAvailableUnits();
      setUnits(floor.units || []);
    }
  }, [visible, floor]);

  const fetchAvailableUnits = async () => {
    try {
      setLoading(true);
      const response = await unitsApi.apiUnitsGet({
        categoryId,
        pageSize: -1,
      });

      // Get IDs of units already on this floor that have available stock
      const existingUnitIds = new Set(
        floor.units?.filter(x => x.available && x.available > 0).map(u => u.unitId) || [],
      );
      console.log(floor.units);
      console.log(existingUnitIds);

      // Filter out units that are already assigned to this floor
      const availableUnitsList = (response.data.items || []).filter(unit => !existingUnitIds.has(unit.id));
      console.log(availableUnitsList);
      setAvailableUnits(availableUnitsList);

      // If no available units, switch to create tab
      if (availableUnitsList.length === 0 && activeTab === 'assign') {
        setActiveTab('create');
      }
    } catch (error) {
      openNotificationWithIcon('error', t('units:fetch-error', 'Failed to fetch units'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignUnit = async (values: any) => {
    try {
      setLoading(true);
      await unitsApi.apiUnitsIdAssignFloorPost({
        id: values.unitId,
        assignUnitToFloorCommand: {
          unitId: values.unitId,
          floorId: floor.id!,
          stock: values.stock,
        },
      });

      // Add newly assigned unit to the table with correct interface
      const assignedUnit = availableUnits.find(u => u.id === values.unitId);
      if (assignedUnit) {
        const existingUnitIndex = units?.findIndex(u => u.unitId === values.unitId);

        if (existingUnitIndex !== undefined && existingUnitIndex >= 0) {
          // Update existing unit's stock
          const updatedUnits = [...(units || [])];
          updatedUnits[existingUnitIndex] = {
            ...updatedUnits[existingUnitIndex],
            stock: (updatedUnits[existingUnitIndex].stock || 0) + values.stock,
            available: (updatedUnits[existingUnitIndex].available || 0) + values.stock,
          };
          setUnits(updatedUnits);
        } else {
          // Add new unit
          setUnits([
            ...(units || []),
            {
              unitId: assignedUnit.id,
              unitName: assignedUnit.name,
              squareMeters: assignedUnit.squareMeters,
              stock: values.stock,
              reserved: 0,
              sold: 0,
              available: values.stock,
            },
          ]);
        }

        // Remove from available units dropdown
        setAvailableUnits(availableUnits.filter(unit => unit.id !== values.unitId));
      }

      openNotificationWithIcon('success', t('units:assigned', 'Unit assigned successfully'));
      assignForm.resetFields();
      await onSuccess();

      // If no more units available, switch to create tab
      if (availableUnits.length <= 1) {
        setActiveTab('create');
      }
    } catch (error) {
      openNotificationWithIcon('error', t('units:assign-error', 'Failed to assign unit'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUnit = async (values: any) => {
    try {
      setLoading(true);
      const createUnitCommand: CreateUnitCommand = {
        name: values.name,
        description: values.description,
        squareMeters: values.squareMeters,
        categoryId: categoryId,
        floorAllocations: [
          {
            categoryFloorId: floor.id!,
            stock: values.stock,
          },
        ],
      };

      const response = await unitsApi.apiUnitsPost({ createUnitCommand });

      // Add newly created unit to the table with correct interface
      if (response.data) {
        setUnits([
          ...(units || []),
          {
            unitId: response.data,
            unitName: values.name,
            squareMeters: values.squareMeters,
            stock: values.stock,
            reserved: 0,
            sold: 0,
            available: values.stock,
          },
        ]);
      }

      openNotificationWithIcon('success', t('units:created', 'Unit created successfully'));
      newUnitForm.resetFields();
      await onSuccess();
    } catch (error) {
      openNotificationWithIcon('error', t('units:create-error', 'Failed to create unit'));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t('units:name', 'Name'),
      dataIndex: 'unitName',
      key: 'unitName',
    },
    {
      title: t('units:square-meters', 'Square Meters'),
      dataIndex: 'squareMeters',
      key: 'squareMeters',
      render: (value: number) => formatSquareMeters(value),
    },
    {
      title: t('units:stock', 'Stock'),
      dataIndex: 'stock',
      key: 'stock',
    },
    {
      title: t('units:reserved', 'Reserved'),
      dataIndex: 'reserved',
      key: 'reserved',
    },
    {
      title: t('units:sold', 'Sold'),
      dataIndex: 'sold',
      key: 'sold',
    },
    {
      title: t('units:available', 'Available'),
      dataIndex: 'available',
      key: 'available',
    },
  ];

  const renderAssignForm = () => (
    <Form form={assignForm} onFinish={handleAssignUnit} layout="vertical">
      <Form.Item
        name="unitId"
        label={t('units:select-unit', 'Select Unit')}
        rules={[{ required: true, message: t('units:select-unit-required', 'Please select a unit') }]}
      >
        <Select loading={loading}>
          {availableUnits.map(unit => (
            <Select.Option key={unit.id} value={unit.id}>
              {unit.name} ({formatSquareMeters(unit.squareMeters || 0)})
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="stock"
        label={t('units:stock', 'Stock')}
        rules={[{ required: true, message: t('units:stock-required', 'Please enter stock amount') }]}
      >
        <InputNumber min={1} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {t('global:assign', 'Assign')}
        </Button>
      </Form.Item>
    </Form>
  );

  const renderCreateForm = () => (
    <Form form={newUnitForm} onFinish={handleCreateUnit} layout="vertical">
      <Form.Item
        name="name"
        label={t('units:name', 'Name')}
        rules={[{ required: true, message: t('units:name-required', 'Please enter unit name') }]}
      >
        <Input />
      </Form.Item>

      <Form.Item name="description" label={t('units:description', 'Description')}>
        <Input.TextArea rows={3} />
      </Form.Item>

      <Form.Item
        name="squareMeters"
        label={t('units:square-meters', 'Square Meters')}
        rules={[{ required: true, message: t('units:square-meters-required', 'Please enter square meters') }]}
      >
        <InputNumber min={0} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        name="stock"
        label={t('units:stock', 'Stock')}
        rules={[{ required: true, message: t('units:stock-required', 'Please enter stock amount') }]}
      >
        <InputNumber min={1} style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {t('global:create', 'Create')}
        </Button>
      </Form.Item>
    </Form>
  );

  return (
    <Modal
      title={
        <div>
          {floor.description || t('categories:floor-number', 'Floor {{number}}', { number: floor.floorNumber })}
          <div
            style={{
              fontSize: '12px',
              color: 'rgba(0, 0, 0, 0.45)',
              marginTop: '4px',
            }}
          >
            {t('categories:manage-units', 'Manage Units')}
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
    >
      <Tabs
        activeKey={activeTab}
        onChange={key => setActiveTab(key as 'assign' | 'create')}
        items={[
          {
            key: 'assign',
            label: (
              <span>
                {t('units:assign-existing', 'Assign Existing Unit')}
                {availableUnits.length === 0 && ` (${t('units:no-available-units', 'No available units')})`}
              </span>
            ),
            children: renderAssignForm(),
            disabled: availableUnits.length === 0,
          },
          {
            key: 'create',
            label: t('units:create-new', 'Create New Unit'),
            children: renderCreateForm(),
          },
        ]}
      />

      <div style={{ marginTop: 16 }}>
        <Table dataSource={units || []} columns={columns} loading={loading} pagination={false} rowKey="id" />
      </div>
    </Modal>
  );
};
